import React, { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { Button, SearchBar, Tooltip } from 'common/components';
import type { TableFilterOption } from '../../CallsTable/CallsTable';
import { CallAnalyticsDateRangeSelector } from '../../CallAnalytics/CallAnalyticsFilters/CallAnalyticsDateRangeSelector';
import { CallAnalyticsMultiSelectFilter } from '../../CallAnalytics/CallAnalyticsFilters/CallAnalyticsMultiSelectFilter';
import { useFixedOnScroll } from '../../CallAnalytics/CallAnalyticsFilters/hooks';
import { SMS_ANALYTICS_DIRECTION_OPTIONS, SMS_ANALYTICS_STATUS_OPTIONS } from './SMSAnalyticsFilters.constants';

import styles from '../../CallAnalytics/CallAnalyticsFilters/CallAnalyticsFilters.module.scss';

export type SMSAnalyticsFilterName = 'leadStatus' | 'smsStatus' | 'smsDirection';

export interface SMSAnalyticsFiltersProps {
    startDate: Date | null;
    endDate: Date | null;
    filterSMSStatus: string;
    filterLeadStatus: string;
    filterSMSDirection: string;
    partyStages?: string[];
    onSearchChange: (value: string) => void;
    onDateRangeTypeChange: (range: string) => void;
    onDateRangeChange: (dateRange: [Date | null, Date | null]) => void;
    onFilterChange: (filter: SMSAnalyticsFilterName, value: string) => void;
    onClear: () => void;
    actionsSlot?: React.ReactNode;
}

export const SMSAnalyticsFilters: React.FC<SMSAnalyticsFiltersProps> = ({
    startDate,
    endDate,
    filterSMSStatus,
    filterLeadStatus,
    filterSMSDirection,
    partyStages = [],
    onSearchChange,
    onDateRangeTypeChange,
    onDateRangeChange,
    onFilterChange,
    onClear,
    actionsSlot,
}) => {
    const [searchBarKey, setSearchBarKey] = useState(0);

    const handleClear = () => {
        setSearchBarKey((key) => key + 1);
        onClear();
    };

    const partyStageOptions = useMemo<TableFilterOption[]>(
        () => partyStages.map((stage) => ({ value: stage, label: stage })),
        [partyStages]
    );

    const {
        containerRef,
        targetRef,
        className: fixedClassName,
        style: fixedStyle,
        placeholderHeight,
    } = useFixedOnScroll(styles.filterBarFixed);

    return (
        <div ref={containerRef} className={styles.filterBarContainer}>
            {placeholderHeight > 0 ? (
                <div className={styles.filterBarPlaceholder} style={{ height: placeholderHeight }} aria-hidden='true' />
            ) : null}
            <div
                ref={targetRef}
                className={`${styles.filterBar} ${fixedClassName}`.trim()}
                style={fixedStyle}
                data-testid='sms-analytics-filters'
            >
                <div className={`${styles.filterControl} ${styles.searchBox}`}>
                    <SearchBar
                        key={searchBarKey}
                        className={styles.searchBar}
                        placeholder='Search by name, number or ID'
                        onSearch={onSearchChange}
                    />
                </div>

                <div className={`${styles.filterControl} ${styles.dateFilterControl}`}>
                    <CallAnalyticsDateRangeSelector
                        startDate={startDate}
                        endDate={endDate}
                        onDateRangeTypeChange={onDateRangeTypeChange}
                        onDateRangeChange={onDateRangeChange}
                    />
                </div>

                <div className={styles.filterControl} data-testid='sms-direction-filter'>
                    <CallAnalyticsMultiSelectFilter
                        label='SMS direction'
                        allOptionLabel='All SMS Directions'
                        selectedValues={[filterSMSDirection]}
                        onChange={(values) => onFilterChange('smsDirection', values[0] ?? '')}
                        options={SMS_ANALYTICS_DIRECTION_OPTIONS.map((option) => ({
                            value: option.value,
                            label: option.label,
                        }))}
                        isMulti={false}
                        aria-label='SMS direction filter'
                    />
                </div>

                <div className={styles.filterControl}>
                    <CallAnalyticsMultiSelectFilter
                        label='SMS status'
                        options={SMS_ANALYTICS_STATUS_OPTIONS}
                        allOptionLabel='All statuses'
                        selectedValues={[filterSMSStatus]}
                        onChange={(values) => onFilterChange('smsStatus', values[0] ?? '')}
                        isMulti={false}
                        isSearchable
                        aria-label='SMS status filter'
                    />
                </div>

                <div className={styles.filterControl}>
                    <CallAnalyticsMultiSelectFilter
                        label='Party stage'
                        isSearchable
                        allOptionLabel='All party stages'
                        options={partyStageOptions}
                        selectedValues={[filterLeadStatus]}
                        onChange={(values) => onFilterChange('leadStatus', values[0] ?? '')}
                        isMulti={false}
                        aria-label='Party stage filter'
                    />
                </div>

                <div className={styles.actions}>
                    <Tooltip content='Clear all filters' position='bottom'>
                        <Button variant='outline-primary' icon={X} onClick={handleClear} iconOnly />
                    </Tooltip>
                    {actionsSlot}
                </div>
            </div>
        </div>
    );
};
